// Vehicle catalog for the booking dropdowns.
// Curated common models for the marques we specialize in, plus "Other".
// Trims are kept short and generic where multiple trims share service profiles.

export const PROBLEM_TYPES = [
  "Check engine light / fault codes",
  "Strange noise (engine / suspension / brakes)",
  "Brake issue (pulsation, squeal, soft pedal)",
  "Suspension / alignment",
  "Cooling / overheating",
  "Oil leak / fluid loss",
  "Transmission / shifting issue",
  "Electrical / battery / lights",
  "Performance loss / misfire / rough idle",
  "Scheduled maintenance (oil, fluids, filters)",
  "Pre-purchase inspection",
  "Tuning / performance work",
  "Carbon clean / walnut blast",
  "Other (describe below)",
];

export const VEHICLE_DB = {
  BMW: {
    models: {
      "1 Series": ["128i", "135i", "M Sport"],
      "2 Series": ["230i", "M240i", "M2", "M2 Competition"],
      "3 Series": ["320i", "328i", "330i", "335i", "M Sport", "M3"],
      "4 Series": ["428i", "430i", "440i", "M4", "M4 Competition"],
      "5 Series": ["528i", "530i", "535i", "540i", "M5", "M5 Competition"],
      "6 Series": ["640i", "650i", "M6"],
      "7 Series": ["740i", "750i", "760Li"],
      "X1": ["sDrive28i", "xDrive28i", "M Sport"],
      "X3": ["xDrive30i", "M40i", "X3M"],
      "X5": ["xDrive40i", "M50i", "X5M"],
      "X6": ["xDrive40i", "M50i", "X6M"],
      "Z4": ["sDrive30i", "M40i"],
      "i3 / i4 / i7 / iX": ["Base", "M Sport", "M50"],
      "Other": ["Other / unknown"],
    },
  },
  Audi: {
    models: {
      "A3 / S3 / RS3": ["A3", "S3", "RS3"],
      "A4 / S4 / RS4": ["A4 Premium", "A4 Premium Plus", "S4", "RS4"],
      "A5 / S5 / RS5": ["A5", "S5", "RS5"],
      "A6 / S6 / RS6": ["A6", "S6", "RS6 Avant"],
      "A7 / S7 / RS7": ["A7", "S7", "RS7"],
      "A8 / S8": ["A8", "S8"],
      "Q3 / SQ3 / RSQ3": ["Q3", "SQ3", "RSQ3"],
      "Q5 / SQ5": ["Q5 Premium", "SQ5"],
      "Q7 / SQ7": ["Q7", "SQ7"],
      "Q8 / SQ8 / RSQ8": ["Q8", "SQ8", "RSQ8"],
      "TT / TTS / TT RS": ["TT", "TTS", "TT RS"],
      "R8": ["V10", "V10 Plus", "V10 Performance"],
      "e-tron / Q4 / Q6 / Q8 e-tron": ["Base", "S", "GT"],
      "Other": ["Other / unknown"],
    },
  },
  Volkswagen: {
    models: {
      "Golf": ["Base", "TSI", "TDI"],
      "Golf GTI": ["S", "SE", "Autobahn", "40th Edition"],
      "Golf R": ["Base", "DCC", "DSG"],
      "Jetta": ["Base", "SE", "GLI"],
      "Passat": ["Base", "SE", "SEL"],
      "Tiguan": ["Base", "SE", "SEL R-Line"],
      "Atlas / Atlas Cross Sport": ["Base", "SE", "SEL"],
      "ID.4": ["Pro", "Pro S", "AWD"],
      "Arteon": ["Base", "SE", "SEL R-Line"],
      "Beetle": ["Base", "Turbo", "Final Edition"],
      "Other": ["Other / unknown"],
    },
  },
  "Mercedes-Benz": {
    models: {
      "A-Class": ["A220", "A35 AMG"],
      "C-Class": ["C300", "C43 AMG", "C63 AMG", "C63 S"],
      "E-Class": ["E350", "E450", "E53 AMG", "E63 AMG / S"],
      "S-Class": ["S500", "S560", "S580", "S63 AMG", "S65 AMG"],
      "CLA / CLS": ["CLA250", "CLA45", "CLS450", "CLS53"],
      "GLA / GLB / GLC": ["GLA250", "GLB250", "GLC300", "GLC43", "GLC63"],
      "GLE / GLS / G-Class": ["GLE350", "GLE450", "GLE63", "GLS450", "G550", "G63 AMG"],
      "SL / SLC / AMG GT": ["SL450", "SL550", "SL63", "AMG GT", "AMG GT-S", "AMG GT-R"],
      "EQS / EQE / EQB": ["Base", "AMG"],
      "Sprinter / Metris": ["Standard", "High Roof"],
      "Other": ["Other / unknown"],
    },
  },
  Subaru: {
    models: {
      "Impreza": ["Base", "Premium", "Sport", "Limited"],
      "WRX": ["Base", "Premium", "Limited", "GT", "tS"],
      "WRX STI": ["Base", "Limited", "Type RA", "S209"],
      "BRZ": ["Premium", "Limited", "tS"],
      "Legacy": ["Base", "Premium", "Sport", "Touring XT"],
      "Outback": ["Base", "Premium", "Onyx XT", "Wilderness"],
      "Forester": ["Base", "Premium", "Sport", "Wilderness"],
      "Ascent": ["Base", "Premium", "Touring"],
      "Crosstrek": ["Base", "Premium", "Sport", "Wilderness"],
      "Other": ["Other / unknown"],
    },
  },
  Porsche: {
    models: {
      "911": ["Carrera", "Carrera S", "Carrera 4S", "Turbo", "Turbo S", "GT3", "GT3 RS", "GT2 RS"],
      "718 Cayman": ["Base", "S", "GTS 4.0", "GT4", "GT4 RS"],
      "718 Boxster": ["Base", "S", "GTS 4.0", "Spyder", "Spyder RS"],
      "Panamera": ["Base", "4S", "GTS", "Turbo", "Turbo S"],
      "Macan": ["Base", "T", "S", "GTS", "Turbo"],
      "Cayenne": ["Base", "S", "GTS", "Turbo", "Turbo GT"],
      "Taycan": ["Base", "4S", "GTS", "Turbo", "Turbo S"],
      "Other": ["Other / unknown"],
    },
  },
  "Other (write-in)": {
    models: { "Other": ["Other / unknown"] },
  },
};

export const MAKES = Object.keys(VEHICLE_DB);

export const YEARS = (() => {
  const max = new Date().getFullYear() + 1;
  const arr = [];
  for (let y = max; y >= 1990; y--) arr.push(String(y));
  return arr;
})();
